import { defineStore } from "pinia"

import { getParamFromUrl } from "@/services/urls.js"

import * as coursesApi from "@/api/coursesApi.js"
import * as lessonsApi from "@/api/lessonsApi.js"

import * as sentryReporter from "@/services/sentryReporter.js"

import i18n from "@/plugins/i18n"

export const useCoursesStore = defineStore("courses", {
  state: () => ({
    courseState: null,
    lessonsState: [],
    loadingCourseState: false,
    loadingCourseFailedState: false,
    loadingLessonsState: false,
    lessonsLoadedState: false,
    lessonsNextCursorState: null,
    activeLessonIdState: null
  }),
  getters: {
    course: (state) => state.courseState,
    lessons: (state) => state.lessonsState,
    loadingCourse: (state) => state.loadingCourseState,
    loadingCourseFailed: (state) => state.loadingCourseFailedState,
    loadingLessons: (state) => state.loadingLessonsState,
    lessonsLoaded: (state) => state.lessonsLoadedState,
    lessonsNextCursor: (state) => state.lessonsNextCursorState,
    activeLessonIndex: (state) => state.lessonsState.findIndex((l) => l.id === state.activeLessonIdState),
    activeLesson(state) {
      return state.lessonsState[this.activeLessonIndex]
    },
    nextLesson(state) {
      return state.lessonsState[this.activeLessonIndex + 1]
    },
    firstUncompleteLessonIndex: (state) => state.lessonsState.findIndex((l) => !l.completed),
    infiniteLoadingPresent: (state) => Boolean(state.lessonsLoadedState && !state.loadingLessonsState && state.lessonsNextCursorState),
    notViewingLastLesson(state) {
      return Boolean(state.lessonsNextCursorState || this.activeLessonIndex < state.lessonsState.length - 1)
    }
  },
  actions: {
    async fetchCourseAndLessons({ courseId }) {
      try {
        this.clearCourseAndLessons()
        await Promise.all([this.fetchCourse({ courseId }), this.fetchLessons({ courseId })])
      } catch (err) {
        sentryReporter.reportError(err, i18n.global.t("common.somethingWrong"))
      }
    },
    async fetchCourse({ courseId }) {
      this.loadingCourseState = true
      try {
        const { course } = await coursesApi.getCourse({ courseId })
        this.courseState = course
      } catch (err) {
        this.loadingCourseFailedState = true
        throw err
      } finally {
        this.loadingCourseState = false
      }
    },
    async resetLessons({ courseId }) {
      this.lessonsState = []
      this.lessonsNextCursorState = null
      await this.fetchLessons({ courseId })
    },
    async fetchLessons({ courseId }) {
      this.loadingLessonsState = true
      try {
        const { lessons, meta } = await lessonsApi.getLessons({
          courseId,
          page: this.lessonsNextCursorState
        })

        this.lessonsNextCursorState = getParamFromUrl(meta.next, "page")
        this.lessonsState = [...this.lessonsState, ...lessons]
      } finally {
        this.lessonsLoadedState = true
        this.loadingLessonsState = false
      }
    },
    async activateNextLesson() {
      if (!this.activeLesson.completed) {
        await this.activeLesson.setCompleted(true)
      }

      if (this.nextLesson) {
        this.setActiveLesson(this.nextLesson.id)
      }

      if (!this.nextLesson && this.lessonsNextCursorState) {
        await this.fetchLessons({ courseId: this.courseState.id })
      }
    },
    setActiveLesson(id) {
      if (this.activeLessonIdState !== id) {
        this.activeLessonIdState = id
        this.courseState.updateCurrentLesson(id)
      }
    },
    restartCourseLessons() {
      this.lessonsState.forEach((lesson) => (lesson.completed = false))
    },
    clearCourseAndLessons() {
      this.courseState = null
      this.lessonsState = []
      this.lessonsNextCursorState = null
      this.activeLessonIdState = null
      this.loadingCourseState = false
      this.loadingCourseFailedState = false
      this.loadingLessonsState = false
      this.lessonsLoadedState = false
    }
  }
})
